import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Authentication } from '../models/authentication';
import { User } from '../models/user';
import { Status } from '../models/status';
import { NotificationService } from './notification.service';
import { AccessStats } from '../models/access-stats';
import { CompactUser } from '../models/compact-user';
import { ChatUser } from '../models/chat-user';
import { AuthenticationStatus } from '../models/authentication-status';
import { OneTimePassword } from '../models/one-time-password';

@Injectable({ providedIn: 'root' })
export class UserService {

    private authenticationSubject: BehaviorSubject<Authentication>;
    public authentication: Observable<Authentication>;

    constructor(private router: Router, private http: HttpClient, private notificationService: NotificationService) {
        this.authenticationSubject = new BehaviorSubject<Authentication>(null);
        this.authentication = this.authenticationSubject.asObservable();
    }

    public get authenticationValue(): Authentication {
        return this.authenticationSubject.value;
    }

    sendOTP(identifier: string, reCaptcha: string) {
        return this.http.post<AuthenticationStatus>(`${environment.API}authentication/send-otp`, { identifier, reCaptcha }).pipe();
    }

    validateOTP(identifier: string, code: string, reCaptcha: string) {
        return this.http.post<any>(`${environment.API}authentication/validate-otp`, { identifier, code, reCaptcha })
            .pipe(map(authentication => {
                this.authenticationSubject.next(authentication);
                this.storeRefreshToken();
                this.startRefreshTokenTimer();
                return authentication;
            }));
    }

    logout() {
        this.http.post<any>(`${environment.API}authentication/revoke`, { token: this.getRefreshToken() }).subscribe();
        this.stopRefreshTokenTimer();
        this.authenticationSubject.next(null);
        this.router.navigate(['/login']);
    }

    kickout() {
        this.stopRefreshTokenTimer();
        this.authenticationSubject.next(null);
        this.router.navigate(['/login']);
        this.notificationService.info('Você foi desconectado por um administrador');
    }

    refreshToken() {
        return this.http.post<any>(`${environment.API}authentication/refresh`, { token: this.getRefreshToken() })
            .pipe(map((authentication) => {
                this.authenticationSubject.next(authentication);
                this.storeRefreshToken();
                this.startRefreshTokenTimer();
                return authentication;
            }));
    }

    getUsers(params?) {
        return this.http.get<User[]>(`${environment.API}users`, { params }).pipe();
    }

    getCompactUsers(params?) {
        return this.http.get<CompactUser[]>(`${environment.API}users/compact`, { params }).pipe();
    }

    getChatUser(params?) {
        return this.http.get<ChatUser[]>(`${environment.API}users/chat-user`, { params }).pipe();
    }

    getUserByID(params) {
        return this.http.get<User>(`${environment.API}users/${params.id ?? params.ID}`).pipe();
    }

    updateUser(userID: number, body) {
        return this.http.put<any>(`${environment.API}users/${userID}`, body).pipe();
    }

    createUser(body) {
        return this.http.post(`${environment.API}users`, body, { responseType: 'text' }).pipe();
    }

    deleteUser(params) {
        return this.http.delete(`${environment.API}users/${params.ID}`).pipe();
    }

    getUserOTPHistory(userID: number, params) {
        return this.http.get<OneTimePassword[]>(`${environment.API}users/${userID}/otp-history`, { params }).pipe();
    }

    uploadImage(image: any) {
        return this.http.post<any>(`${environment.API}users/upload-image`, image).pipe();
    }

    logoutFromAllSessions(userID: number) {
        return this.http.post<any>(`${environment.API}authentication/logout`, { userID: userID }).pipe(concatMap(auth => {

            if (userID == this.authenticationValue.userID) {
                this.stopRefreshTokenTimer();
                this.authenticationSubject.next(null);
                this.router.navigate(['/login']);
            }

            return auth;
        }))
    }

    getStatus() {
        return this.http.get<Status[]>(`${environment.API}users/status`).pipe();
    }

    updateUserStatus(body) {
        return this.http.put<any>(`${environment.API}users/status`, body).pipe();
    }

    getAccessStats(params) {
        return this.http.get<AccessStats>(`${environment.API}users/access-stats`, { params }).pipe();
    }

    // helper methods

    private refreshTokenTimeout: any;

    private startRefreshTokenTimer() {
        // parse json object from base64 encoded jwt token
        const jwtToken = JSON.parse(atob(this.authenticationValue.jwtToken.split('.')[1]));

        // set a timeout to refresh the token a minute before it expires
        const expires = new Date(jwtToken.exp * 1000);
        const timeout = expires.getTime() - Date.now() - (60 * 1000);
        this.refreshTokenTimeout = setTimeout(() => this.refreshToken().subscribe(), timeout);
    }

    private stopRefreshTokenTimer() {
        clearTimeout(this.refreshTokenTimeout);
    }

    private storeRefreshToken() {
        localStorage.setItem('rft-vt', this.authenticationValue.refreshToken);
    }

    private getRefreshToken() {
        return localStorage.getItem('rft-vt');
    }
}
